import React, { ReactElement, ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { StandardButton, StandardButtonType } from '@payer-admin-ui/components-v2/src/buttons/StandardButton';
import { PayerError } from '../utils/errors';
import { PayerErrorMessage } from './PayerErrorMessage';

export type ErrorDialogProps = {
  open: boolean;
  onClose: () => void;
  onClosed?: () => void;
  children: ReactNode;
};

export const ErrorDialog = ({ open, onClose, onClosed, children }: ErrorDialogProps): ReactElement => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onClose={onClose} onTransitionExited={onClosed} fullWidth maxWidth="sm">
      <DialogTitle>{t('error.modal.title')}</DialogTitle>
      <DialogContent>{children}</DialogContent>
      <DialogActions>
        <StandardButton type={StandardButtonType.CLOSE} onClick={onClose} />
      </DialogActions>
    </Dialog>
  );
};

export type PayerErrorDialogProps = {
  error: PayerError | null;
  onClose: () => void;
  onClosed?: () => void;
};

export const PayerErrorDialog = ({ error, onClose, onClosed }: PayerErrorDialogProps): ReactElement => {
  /**
   * The following lines contain a semi-hacky solution to avoid removing the error message from the dialog before its
   * exit transition finishes and it closes, causing a slight and ugly flicker when the message itself is removed
   * before the modal closes. With this solution, we may keep the same interface but still cause the modal to close
   * more gracefully.
   */
  const [open, setOpen] = useState(false);
  const [storedError, setStoredError] = useState<PayerError>();

  useEffect(() => {
    if (storedError && !error) {
      setOpen(false);
    } else if (!storedError && error) {
      setStoredError(error);
      setOpen(true);
    }
  }, [error, setOpen, storedError]);

  return (
    <ErrorDialog
      open={open}
      onClose={onClose}
      onClosed={() => {
        onClosed?.();
        setStoredError(undefined);
      }}
    >
      {storedError && <PayerErrorMessage error={storedError} />}
    </ErrorDialog>
  );
};
